import React, { useCallback, useContext, useEffect, useState } from 'react';
import styles from './productHome.module.scss';
import i18n from 'i18n';
import { Form, Row, Col, Button } from 'react-bootstrap';
import Fade from 'react-bootstrap/Fade';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { useCallAPI } from 'hooks/useCallAPI';
import Select from 'components/Select/Select';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'components/Modal/Modal';
import { ProductHome } from 'containers/RetailMomo/Products/ProductHome';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { SelectOptions } from 'components/commonType';
import { useOrderStore } from 'store/orderStore';
import { ProductListDataContext } from '../ProductListDataContext';
import { useCampaignStore } from 'store/campaignStore';

/**
 *
 * @param {string} props.title
 * @param {string} props.type
 * @param {object} props.campaign
 * @returns
 * @description MaxCVRHome component
 * @since 2024-10-25
 * @example
 * <MaxCVRHome title={'商品投放模式'} type={'edit'} campaign={campaign} />
 * @export
 * @component
 * @category RetailMomo
 * @subcategory Products
 * @namespace MaxCVRHome
 * @version 0.0.1
 * @property {string} [title=i18n.t<string>('productHome.labels.title')] - 標題
 * @property {string} type - 編輯模式
 * @property {object} campaign - 活動資料
 * @property {boolean} [loading] - loading
 * @property {function} [callAPIs] - callAPIs
 * @property {function} [selectedProducts] - 手動選品的商品清單
 * @property {function} [handleSelectedProducts] - 修改context手動選品的商品清單
 * @property {function} [defaultBrandManager] - The defaultBrandManager function
 * @property {array} [brandOptions] - 品牌清單
 * @property {boolean} [disabledAutoPattern] - disabled智能選品
 * @property {boolean} [disabledSelectPattern] - disabled手動選品
 * @property {boolean} [disabledSelectBrand] - disabled品牌下拉選單
 * @property {number} [selectPattern] - 選擇商品投放模式
 * @property {number} [selectBrand] - 選擇品牌
 * @property {boolean} [show] - 選擇商品Modal狀態
 * @property {function} [changeSelectProductType] - 切換商品投放模式
 * @property {function} [changBrand] - 切換品牌
 * @property {function} [changeModal] - 開啟/關閉選擇商品Modal
 * @property {function} [fetchMaxCvrBrandOptions] - 取得智能選品品牌清單
 */
export const MaxCVRHome = (props) => {
  const defaultBrandManager = new DefaultBrandManager();
  const {
    title = i18n.t<string>('productHome.labels.title'),
    type,
    campaign,
  } = props;
  const { loading, callAPIs } = useCallAPI();
  const { selectedProducts } = useContext(ProductListDataContext);
  const [brandOptions, setBrandOptions] = useState<SelectOptions[]>([]);
  const [disabledAutoPattern, setDisabledAutoPattern] =
    useState<boolean>(false);
  const [disabledSelectBrand, setDisabledSelectBrand] =
    useState<boolean>(false);
  const [selectPattern, setSelectPattern] = useState<string>(
    campaign.basic.selectPattern ? campaign.basic.selectPattern : null,
  );
  const [selectBrand, setSelectBrand] = useState<object | null>(
    campaign.basic.webBrandNo
      ? { value: campaign.basic.webBrandNo, label: campaign.basic.brandName }
      : { value: 1, label: '全部' },
  );
  const [show, setShow] = useState<boolean>(false);
  const { lockBrand } = useCampaignStore();
  const changeSelectProductType = (event) => {
    campaign.basic.selectPattern = event.target.value;
    campaign.basic.bidPrice = null;
    setSelectPattern(event.target.value);
    if (type === 'create' && campaign.basic.selectPattern === 'MANUAL') {
      campaign.basic.webBrandNo = null;
      campaign.basic.brandName = null;
      setSelectBrand({ value: 1, label: '全部' });
    }
  };

  const changBrand = (event) => {
    if (event.value === 1) {
      campaign.basic.webBrandNo = null;
      campaign.basic.brandName = null;
    } else {
      campaign.basic.webBrandNo = event.value;
      campaign.basic.brandName = event.label;
    }
    campaign.basic.bidPrice = null;
    setSelectBrand(event);
  };

  const changeModal = () => {
    setShow(!show);
  };

  const fetchMaxCvrBrandOptions = useCallback(async (): Promise<void> => {
    try {
      await callAPIs(
        [defaultBrandManager.getMaxCvrBrandOptions.bind(defaultBrandManager)],
        (brandOptions) => {
          if (
            brandOptions.some(
              ({ value }) => value === campaign.basic.webBrandNo,
            )
          ) {
            campaign.basic.brandName = brandOptions.filter(
              ({ value }) => value === campaign.basic.webBrandNo,
            )[0].label;
            setSelectBrand({
              value: campaign.basic.webBrandNo,
              label: campaign.basic.brandName,
            });
          }
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          const options = [
            { value: 1, label: '全部' },
            ...preprocessBrandOptions,
          ];
          if (options.length === 1)
            setDisabledSelectBrand(options.length === 1 ? true : false);
          setBrandOptions(options);
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [callAPIs]);

  useEffect(() => {
    fetchMaxCvrBrandOptions();
  }, []);

  useEffect(() => {
    switch (type) {
      case 'edit':
        break;
      case 'create':
        // 新增模式下若只有一個品牌並且為"其他"則預設為手動選品，並且無法選擇智能選品
        if (
          brandOptions.some(({ value }) => value === '99999999999999999') &&
          brandOptions.length === 1
        ) {
          campaign.basic.selectPattern = 'MANUAL';
          setSelectPattern('MANUAL');
          setDisabledAutoPattern(true);
          return;
        } else if (campaign.basic.selectPattern === null) {
          campaign.basic.selectPattern = 'AUTO';
          setSelectPattern('AUTO');
          return;
        }
        break;
      default:
        break;
    }

    if (brandOptions.length === 1 && brandOptions[0].value === 1) {
      campaign.basic.selectPattern = 'MANUAL';
      setSelectPattern('MANUAL');
      setDisabledAutoPattern(true);
      return;
    }
  }, [brandOptions, disabledSelectBrand]);

  useEffect(() => {
    if (type === 'edit' && lockBrand) {
      setDisabledSelectBrand(true);
    }
  }, [type, lockBrand]);

  return (
    <>
      {loading && <LoadingIndicator />}
      <fieldset style={{ marginTop: 40 }}>
        <legend className={styles.title}>
          <span>{title}</span>
        </legend>
      </fieldset>
      <div className={styles.container}>
        <div className={styles.titleArea}>
          <Form.Group
            as={Row}
            controlId={'age-opt-preferred'}
            style={{ marginLeft: 0, marginRight: 0 }}
          >
            <Col sm={2}>
              <Form.Label style={{ marginTop: 0 }}>
                {'商品投放模式選擇'}
              </Form.Label>
            </Col>
            <Col sm={3}>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>(
                    'rtbCampaign.limitation.operationSet.autoSelectProduct',
                  )}
                  value={'AUTO'}
                  name='selectPattern'
                  id={'auto_selectProduct'}
                  onChange={changeSelectProductType}
                  checked={selectPattern === 'AUTO'}
                  style={{ paddingLeft: 0 }}
                  disabled={disabledAutoPattern}
                />
                <Form.Text
                  as={'span'}
                  className={styles.description}
                  style={{ paddingLeft: '1.5rem' }}
                  muted
                >
                  動態調整投廣商品 ( 此功能僅限部分廣告主 )
                </Form.Text>
              </div>
            </Col>
            <Col sm={3}>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>(
                    'rtbCampaign.limitation.operationSet.customSelectProduct',
                  )}
                  value={'MANUAL'}
                  name='selectPattern'
                  id={'custom_selectProduct'}
                  onChange={changeSelectProductType}
                  checked={selectPattern === 'MANUAL'}
                  style={{ paddingLeft: 0 }}
                  disabled={false}
                />
                <Form.Text
                  as={'span'}
                  className={styles.description}
                  style={{ paddingLeft: '1.5rem' }}
                  muted
                >
                  手動選擇投廣商品
                </Form.Text>
              </div>
            </Col>
          </Form.Group>
          {selectPattern === 'AUTO' && (
            <Fade in={selectPattern === 'AUTO'}>
              <Form.Group
                as={Row}
                controlId={'brand-opt-preferred'}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                <Col sm={2}>
                  <Form.Label>
                    {'品牌選擇'}{' '}
                    <IconWithTooltip
                      icon={faQuestionCircle}
                      placement='bottom-start'
                      tooltipProps={{
                        id: 'selected-brand-hint',
                        tooltip: i18n.t<string>(
                          'productHome.filters.hints.tags',
                        ),
                      }}
                    />
                  </Form.Label>
                </Col>
                <Col>
                  <Select
                    options={brandOptions}
                    value={selectBrand}
                    onChange={changBrand}
                    simpleValue={false}
                    isDisabled={disabledSelectBrand}
                  />
                  <Form.Text as={'span'} className={styles.description} muted>
                    此清單僅列出符合智能選品資格的品牌，若欲投廣此帳號下其他未列出品牌，請改採手動選品
                  </Form.Text>
                  <Form.Text as={'span'} className={styles.description} muted>
                    建立活動後，即不可更換品牌
                  </Form.Text>
                </Col>
              </Form.Group>
            </Fade>
          )}
          {selectPattern === 'MANUAL' && (
            <Fade in={selectPattern === 'MANUAL'}>
              <Form.Group
                as={Row}
                controlId={'product-opt-preferred'}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                <Col sm={2}>
                  <Form.Label>{'商品選擇'}</Form.Label>
                </Col>
                <Col sm={3}>
                  <button
                    className={styles.selectProduct}
                    onClick={changeModal}
                  >
                    {selectedProducts.length === 0
                      ? '請選擇商品'
                      : `已選商品(${selectedProducts.length}/25)`}
                    <IconWithTooltip
                      placement='right'
                      className={styles.productEditBtn}
                      icon={faPencilAlt}
                      tooltipProps={{
                        id: `campaignListCreativeEditHint`,
                        tooltip: i18n.t<string>(
                          'campaignList.labels.creativeEditHint',
                        ),
                      }}
                    />
                  </button>
                </Col>
              </Form.Group>
            </Fade>
          )}
        </div>
      </div>
      {show && (
        <Modal title={'選擇商品'} fullScreen={true} dismiss={changeModal}>
          <ProductHome
            defaultFilters={{
              advertiser: useOrderStore.getState().order.advertiserId,
            }}
            enableCreateProductNativeCreative={false}
            limitSelect={true}
          />
          <div className={styles.buttonArea}>
            <Button variant='primary' size='sm' onClick={changeModal}>
              {i18n.t<string>('common.buttons.confirm')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
